import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useOutletContext, useSearchParams } from 'react-router';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import type { PlayerPoolTier } from '@sorare/core/src/lib/playerPoolTiers';
import { craftingPoolVariables } from '@sorare/core/src/lib/playerPoolTiers';

import { PlayerWithSupply } from '#src/components/rewards/PlayerWithSupply/index.tsx';
import { COMPETITION_SEARCH_PARAM } from '#src/components/rewards/PoolCompetitionFilter/index.tsx';
import type { CraftingPoolOutletContext } from '#src/layouts/rewards/RewardsEssenceLayout/index.tsx';

import type {
  RewardsEssenceTierRarityPageQuery,
  RewardsEssenceTierRarityPageQueryVariables,
} from './__generated__/index.graphql.ts';

const REWARDS_ESSENCE_TIER_RARITY_PAGE_QUERY = gql`
  query RewardsEssenceTierRarityPageQuery(
    $sport: Sport!
    $rarity: Rarity!
    $quality: CardQuality
    $gameplayTier: GameplayTier
    $competitionSlug: String
  ) {
    cardShardsPool(
      sport: $sport
      rarity: $rarity
      quality: $quality
      gameplayTier: $gameplayTier
      competitionSlug: $competitionSlug
    ) {
      slug
      ...PlayerWithSupply_playerWithSupply
    }
  }
  ${PlayerWithSupply.fragments.playerWithSupply}
` as TypedDocumentNode<
  RewardsEssenceTierRarityPageQuery,
  RewardsEssenceTierRarityPageQueryVariables
>;

type Props = {
  tier: PlayerPoolTier;
  rarity: Rarity;
};

export const RewardsEssenceTierRarityPage = ({ tier, rarity }: Props) => {
  const { sport } = useSportContext();
  const [searchParams] = useSearchParams();
  const { competitionNotCraftableYet } =
    useOutletContext<CraftingPoolOutletContext>();
  const { data, loading } = useQuery(REWARDS_ESSENCE_TIER_RARITY_PAGE_QUERY, {
    variables: {
      sport,
      rarity,
      competitionSlug: searchParams.get(COMPETITION_SEARCH_PARAM) || undefined,
      ...craftingPoolVariables(tier),
    },
  });
  const { cardShardsPool } = data || {};

  if (loading) {
    return <LoadingIndicator />;
  }

  // The layout already explains why an upcoming competition has no Cards.
  if (
    !cardShardsPool ||
    (competitionNotCraftableYet && !cardShardsPool.length)
  ) {
    return null;
  }

  return <PlayerWithSupply players={cardShardsPool} rarity={rarity} />;
};
